// preview_frame — la largeur de l'aperçu, et à qui la console fait confiance.
//
// ── LES TROIS LARGEURS ──────────────────────────────────────────────────────
//
// Sa demande, le 2026-10-05 : « téléphone / tablette / bureau dans la barre : tu bascules et tu
// vois ». C'est ce qui rend VISIBLE la promesse de responsivité AU MOMENT du choix, au lieu de
// la promettre après coup.
//
// Les valeurs ne sont pas choisies au goût : ce sont trois des sept largeurs que
// scripts/viewport_audit mesure déjà. Prévisualiser une largeur que rien ne vérifie donnerait
// une confiance qu'on n'a pas, et un test tient cet accord.
//
// ── ET LA DÉCISION DE SÛRETÉ ────────────────────────────────────────────────
//
// La console reçoit des messages de l'aperçu (visual_bridge). Elle doit donc savoir quelle
// origine est la sienne, et `previewOrigin` répond `null` dès qu'elle n'en est pas sûre : le
// pont refuse alors tout, puisqu'une liste d'origines vide refuse tout. Mieux vaut un éditeur
// qui ne s'allume pas qu'un éditeur qui écoute n'importe qui.

export type PreviewWidth = {
  readonly id: string;
  readonly label: string;
  /** La largeur en pixels, ou `null` pour « toute la place ». */
  readonly px: number | null;
};

/** Du plus petit au plus grand : c'est aussi l'ordre des boutons, comme partout ailleurs.
 *
 *  `null` pour le bureau plutôt que 1600 : le volet de l'opérateur fait ce qu'il fait, et une
 *  largeur fixe y laisserait une bande vide. */
export const PREVIEW_WIDTHS: readonly PreviewWidth[] = [
  { id: "phone",   label: "Phone",   px: 390 },
  { id: "tablet",  label: "Tablet",  px: 768 },
  { id: "desktop", label: "Desktop", px: null },
];

/** PURE. Le style à poser sur le cadre de l'aperçu.
 *
 *  Centré, parce qu'un téléphone de 390 px collé au bord gauche d'un volet de 900 se lit comme
 *  une erreur d'affichage. Une mesure absurde vaut « toute la place » : c'est le repli qui ne
 *  casse rien. */
export function widthStyle(px: number | null): { width?: string; marginInline?: string } {
  if (px === null || !Number.isFinite(px) || px <= 0) return {};
  return { width: `${px}px`, marginInline: "auto" };
}

/** Les deux seuls schémas dont une origine est utilisable ici. `data:` et `file:` rendent la
 *  CHAÎNE « null », qui ressemble à une origine valable et n'en est pas une. */
const SCHEMAS = new Set(["http:", "https:"]);

/**
 * PURE. L'origine de l'aperçu, ou `null` quand on n'en est pas sûr.
 *
 * Le port compte : les aperçus dev vivent sur un port par slot, et confondre deux ports ferait
 * écouter l'app d'un autre client.
 */
export function previewOrigin(url: string): string | null {
  try {
    const u = new URL(url);
    if (!SCHEMAS.has(u.protocol)) return null;
    return u.origin;
  } catch {
    return null;
  }
}
